'use client'
import React, { useCallback, useMemo } from 'react';
import { Flex, Text, Image, Box, Button, useDisclosure, Heading, Show,   Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal } from "@chakra-ui/react";
import "./Header_home.css";
import { GoogleForm, Navbarforhome } from "..";
import { FaDiscord } from "react-icons/fa";
import { Metadata } from 'next';
import { useAppContext } from 'hooks';
import { useRouter } from 'next/navigation';
import { throttle } from 'lodash';
import { ChevronDownIcon } from "@chakra-ui/icons";

export const generateMetadata = (): Metadata => ({
  title: { absolute: "DeepMode | AI Character Creation & Art Generator" },
  description: "Create unique AI-generated art with DeepMode. Our advanced AI models let you design custom characters and produce stunning digital images. Explore the future of AI-powered creativity today.",
});

const Header_home = ({ data }: any) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { jwt } = useAppContext();
  const router = useRouter();

  // Wrap the function with throttle
  const handleStartCreatingClick = useCallback(
    throttle(() => {
      if (jwt) {
        router.push('/dashboard');
      } else {
        onOpen();
      }
    }, 2000),
    [jwt, onOpen, router]  // Add dependencies here
  );
  

  return (
    <>
      {/* Mobile, < 768px */}
      <Show below="md">
        <Flex bg="#050918" direction="column">
          <Flex justify="space-between" align="center" p="0px" px="32px" mt="20px">
           <Image
              src="DeepModenew.svg"
              alt="DeepMode Logo"
              width="128px"
              height="28.92px"
            />
             {jwt ? (
                  // If logged in: show a Menu button (Dashboard, Public Models, Gallery)
                  <Menu>
                    <MenuButton
                      as={Button}
                      w="90px"  // 👈 Width same as Dashboard button
                      h="40px"  // 👈 Height same
                      px="10px"
                      fontSize="12px"
                      color="white"
                      bg="#FF1B6D"
                      _hover={{ bg: "#FF5E9C" }}
                      _active={{ bg: "#FF5E9C" }} 
                      borderRadius="12px"
                      rightIcon={<ChevronDownIcon />}
                    >
                      Menu
                    </MenuButton>
                    <Portal>
                      <MenuList
                        borderRadius="12px"
                        p="8px"
                        bg="gray.700"
                        border="1px solid #FF1B6D"
                        mt="8px"
                      >
                        <MenuItem
                          bg="transparent"
                          _hover={{ bg: "gray.600" }}
                          onClick={() => router.push("/dashboard")}
                        >
                          Dashboard
                        </MenuItem>
                        <MenuItem
                          bg="transparent"
                          _hover={{ bg: "gray.600" }}
                          onClick={() => router.push("/public-models")}
                        >
                          Public Models
                        </MenuItem>
                        <MenuItem
                          bg="transparent"
                          _hover={{ bg: "gray.600" }}
                          _focus={{ bg: "gray.600" }}
                          onClick={() => router.push("/gallery")}
                        >
                          <Flex align="center" gap="2px">
                            <Text>Gallery</Text>
                            <Box
                              ml="1"
                              px="6px"
                              py="2px"
                              bg="purple.400"
                              color="white"
                              fontSize="10px"
                              fontWeight="bold"
                              borderRadius="8px"
                              lineHeight="1"
                            >
                              NEW!
                            </Box>
                          </Flex>
                        </MenuItem>
                      </MenuList>
                    </Portal>
                  </Menu>
                ) : (
                  // If not logged in: show Sign Up button
                  <Button
                    h="40px"
                    px="20px"
                    fontSize="14px"
                    color="white"
                    bg="#FF1B6D"
                    borderRadius="12px"
                    onClick={handleStartCreatingClick}
                  >
                    Sign Up
                  </Button>
                )}

          </Flex>
          <Flex
            direction="column"
            textAlign="center"
            align="center"
            pt="30px"
            backgroundImage={data?.BackgroundImageMobile?.data?.attributes?.url}
            backgroundSize="contain"
            opacity="1"
            mixBlendMode="lighten"
          >
            <Flex align="center">
              <Flex align="center" direction="column">
                <h1 style={{ fontSize: "38px", fontWeight: "bold", color: "white", textAlign: "center", lineHeight: "1.1"  }}>
                  {data.Headline}
                  <br />
                  <span
                    style={{
                      background: "linear-gradient(to right, #FFE1FF 10%, #A4DBEE 40%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontWeight: "extrabold",
                    }}
                  >
                    {data.HeadlineNewline}
                  </span>
                </h1>
                <Heading
                  as="h2"
                  textAlign="center"
                  maxW="320px"
                  fontSize="13px"
                  color="white"
                  mt="15px"
                >
                  {data.SubHeader}
                </Heading>
                <GoogleForm isOpen={isOpen} onClose={onClose} />
                <Flex direction={{ base: "column", md: "row" }} mt="20px" align="center">
                  <Button
                    p="18px"
                    h="58px"
                    onClick={handleStartCreatingClick}
                    maxW={{ base: "100%", md: "180px" }}
                    minW={{ base: "100%", md: "180px" }}
                    color="white"
                    bg="button.500"
                    _hover={{ opacity: "0.9" }}
                  >
                    {data.PrimaryButton}
                  </Button>
                  <a href="https://discord.gg/CMwtccKthy" target="_blank">
                    <Button
                      mt={{ base: "15px", md: 0 }}
                      ml={{ base: 0, md: "5px" }}
                      borderRadius="12px"
                      p="18px"
                      h="58px"
                      maxW={{ base: "209px", md: "180px" }}
                      minW={{ base: "209px", md: "180px" }}
                      leftIcon={<FaDiscord size="28px" />}
                      color="#5865F2"
                      variant="outline"
                      borderColor="#5865F2"
                      borderWidth="2px"
                    >
                      {data.SecondaryButton}
                    </Button>
                  </a>
                </Flex>
              </Flex>
            </Flex>
            <Flex justify="center" align="center" mt="30px" width="100%">
              <Box width={{ base: "90%", md: "auto" }} textAlign="center" mx="auto">
                <Image
                  src={data?.ImageArrayMobile?.data?.attributes?.url}
                  alt={data?.ImageArrayMobile?.data?.attributes?.alternativeText || "Default alt text"}
                  objectFit="contain"
                  w="100%"
                  h="auto"
                />
              </Box>
            </Flex>
          </Flex>
          <Flex
            color="white"
            px={["25px", "35px", "40px", "60px", "60px", "22%"]}
            py="26px"
            overflow="hidden"
            justify="start"
            fontWeight="semibold"
            position="relative"
            w="100%"
            bg="transparent"
          >
            <div className="scrolling-text">
              <Text>
                ✨ Every photo is unique! ✨ Endless generations! ✨ Endless Variations ✨ Unimaginable scenes
              </Text>
            </div>
          </Flex>
        </Flex>
      </Show>
      {/* Desktop, >= 768px */}
      <Show above="md">
        <Flex direction="column">
          <Flex
            opacity={0.9}
            h="100%"
            direction="column"
            backgroundRepeat="no-repeat"
            bg="#050918"
            position="relative"
          >
            <Box
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="60%"
              backgroundImage={data?.BackgroundImage?.data?.attributes?.url}
              backgroundSize="contain"
              opacity="1"
              mixBlendMode="lighten"
              zIndex="0"
            />
            <Navbarforhome />
            <Flex
              px={["25px", "35px", "40px", "60px", "60px", "15%"]}
              justify="center"
              align="center"
              mt="30px"
            >
              <Flex
                minW={{ base: "300px", md: "390px", lg: "400px", xl: "550px" }}
                direction="column"
                justify="center"
                align="center"
                h="100%"
              >
                <h1 style={{ fontSize: "3.75rem", fontWeight: "bold", color: "white", textAlign: "center", lineHeight: 1.2 }}>
                  {data.Headline}
                  <br />
                  <span
                    style={{
                      background: "linear-gradient(to right, #FFE1FF 10%, #A4DBEE 40%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontWeight: "extrabold",
                    }}
                  >
                    {data.HeadlineNewline}
                  </span>
                </h1>
                <GoogleForm isOpen={isOpen} onClose={onClose} />
                <Text
                  maxW={{ base: "350px", md: "470px", "2xl": "600px" }}
                  color="white"
                  fontWeight={500}
                  fontSize={{
                    sm: "14px",
                    md: "16px",
                    lg: "18px",
                    "2xl": "20px",
                  }}
                  textAlign="center"
                  mt="10px"
                >
                  {data.SubHeader}
                </Text>
                <Flex mt="20px" align="center">
                  <Button
                    p="18px"
                    h="58px"
                    w="200px"
                    borderRadius="12px"
                    onClick={handleStartCreatingClick}
                    color="white"
                    bg="button.500"
                    _hover={{ opacity: "0.9" }}
                  >
                    {jwt ? `${data.PrimaryButton}` : "Start Creating for free"}
                  </Button>

                  <a href="https://discord.gg/CMwtccKthy" target="_blank">
                    <Button
                      ml="10px"
                      borderRadius="12px"
                      p="18px"
                      h="58px"
                      maxW="180px"
                      leftIcon={<FaDiscord size="28px" />}
                      color="#5865F2"
                      variant="outline"
                      borderColor="#5865F2"
                      borderWidth="2px"
                    >
                      {data.SecondaryButton}
                    </Button>
                  </a>
                </Flex>

              </Flex>
            </Flex>
            <Image
              mt="60px"
              src={data?.ImageArray?.data?.attributes?.url}
              alt={data?.ImageArray?.data?.attributes?.alternativeText || "Default alt text"}
              objectFit="contain"
              w="100%"
              h="auto"
            />

          </Flex>
          <Flex
            color="white"
            px={["25px", "35px", "40px", "60px", "60px", "12%"]}
            py="26px"
            bg="transparent"
            justify="space-between"
          >
            <Text>✨ Every photo is unique!</Text>
            <Text>✨ Endless generations!</Text>
            <Text>✨ Endless Variations!</Text>
            <Text>✨ Unimaginable scenes!</Text>
          </Flex>
        </Flex>
      </Show>
    </>
  );
};

export default Header_home;
